/**
 * Input style mappings. No styles in the component; all class maps live here.
 * Size controls height and font; each size is responsive (smaller on mobile, scales on sm/md/lg).
 *
 * @internal Used by Input atom only.
 */

import { cn } from '@/utils/cn';
import type { InputRounded, InputSize } from './Input.types';

const BASE_CLASSES =
  'block w-full border border-light-lila bg-light placeholder:text-dark-invirtus/40 focus:outline-none focus:border-purple focus:ring-2 focus:ring-purple/30 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-200';

/** Responsive height, padding, font size and line-height per size. */
const SIZE_CLASSES: Record<InputSize, string> = {
  sm: 'h-7 sm:h-8 px-2.5 sm:px-3 text-xs leading-7 sm:leading-8',
  md: 'h-8 sm:h-9 md:h-10 px-3 sm:px-4 text-xs sm:text-sm leading-8 sm:leading-9 md:leading-10',
  lg: 'h-9 sm:h-10 md:h-11 px-3 sm:px-4 text-sm sm:text-base leading-9 sm:leading-10 md:leading-11',
};

/** Each size's largest-breakpoint values, applied at every viewport. */
const FIXED_SIZE_CLASSES: Record<InputSize, string> = {
  sm: 'h-8 px-3 text-xs leading-8',
  md: 'h-10 px-4 text-sm leading-10',
  lg: 'h-11 px-4 text-base leading-11',
};

const ROUNDED_CLASSES: Record<InputRounded, string> = {
  full: 'rounded-full',
  lg: 'rounded-lg',
  md: 'rounded-md',
  sm: 'rounded-sm',
};

const ERROR_BORDER = 'border-red-soft';

export function getInputClasses(
  size: InputSize,
  rounded: InputRounded,
  hasError: boolean,
  className?: string,
  isResponsive = true
): string {
  return cn(
    BASE_CLASSES,
    isResponsive ? SIZE_CLASSES[size] : FIXED_SIZE_CLASSES[size],
    hasError && ERROR_BORDER,
    ROUNDED_CLASSES[rounded],
    className
  );
}